import { X, Link } from '@phosphor-icons/react';
import { Assigned } from '@Models/types';
import DOMPurify from 'dompurify';

interface ItemModalProps {
  isOpen: boolean;
  onCancel: () => void;
  record: Assigned | null;
  onTag: (record: Assigned) => void;
}

const ItemModal = ({ isOpen, onCancel, record, onTag }: ItemModalProps) => {
  if (!record) return null;

  return (
    <div
      className={`${
        isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
      } fixed inset-0 z-50 flex h-full w-full items-center justify-center bg-gray-100 bg-opacity-70 transition-opacity duration-300`}
    >
      <div
        className={`flex w-1/2 transform flex-col rounded-xl bg-white pb-12 shadow-xl transition-transform duration-300 ${
          isOpen ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}
      >
        <div className="flex h-12 items-center justify-between rounded-tl-lg rounded-tr-lg bg-primary-700 px-4 py-3">
          <p className="font-semibold text-white">{record.reference_num}</p>
          <div className="flex">
            <X
              onClick={onCancel}
              size={20}
              className="cursor-pointer text-white hover:text-red-500"
            />
          </div>
        </div>
        <div className="relative flex max-h-[70vh] flex-col gap-4 overflow-y-auto overflow-x-hidden rounded-bl-lg rounded-br-lg bg-white font-inter text-primary-700">
          <div className="custom-scroll overflow-x-auto pl-5 pr-5 pt-2">
            {' '}
            <div>
              <p className="pb-5">
                <span className="font-semibold">Title:</span>{' '}
                <span className="font-normal hover:text-primary-500 hover:underline">
                  <a
                    href={record.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className=""
                    title={record.url}
                  >
                    {record.name}
                    <span className="relative ml-2">
                      <span className="absolute">
                        <Link size={21} />
                      </span>
                    </span>
                  </a>
                </span>
              </p>
              <p className="mt-1 font-semibold">Description:</p>
              <div
                className="mt-1 indent-8"
                dangerouslySetInnerHTML={{
                  __html:
                    DOMPurify.sanitize(record.description || '') ||
                    'No description available',
                }}
              ></div>
            </div>
            <div>
              <p className="font-semibold">Created At</p>
              <p className="mt-1">
                {new Date(record.created_at || '').toLocaleString()}
              </p>
            </div>
            {record.gptClassification && (
              <div>
                <p className="font-semibold">AI Classification</p>
                <div className="mt-1 indent-8">
                  <p>
                    <strong>Confidence Score:</strong>{' '}
                    {record.gptClassification.confidence_score}
                  </p>
                  <p>
                    <strong>Production Bug:</strong>{' '}
                    {record.gptClassification.production_bug}
                  </p>
                  <p>
                    <strong>Explanation:</strong>{' '}
                    {record.gptClassification.explanation}
                  </p>
                  {record.gptClassification.error && (
                    <p>
                      <strong>Status:</strong> {record.gptClassification.error}
                    </p>
                  )}
                </div>
              </div>
            )}
            <div className="fixed bottom-4 right-4 flex">
              {' '}
              <button
                onClick={() => onTag(record)}
                className="flex w-10 items-center justify-center rounded-md bg-primary-100 px-8 py-1 text-center text-sm font-semibold text-primary-700 opacity-50 hover:opacity-100"
              >
                Tag
              </button>
            </div>
          </div>
        </div>
        <style>{`
        .custom-scroll::-webkit-scrollbar {
          height: 10px;
        }
      `}</style>
      </div>
    </div>
  );
};

export default ItemModal;
